import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Key, Plus, Trash2, Copy, Webhook, Loader2, ExternalLink, RefreshCw } from "lucide-react";
import { Link } from "@/lib/router-compat";
import { useAuth } from "@/contexts/AuthContext";

const ALL_SCOPES = [
  "read:proposals", "write:proposals",
  "read:threats", "write:threats",
  "read:contracts", "write:contracts",
  "read:inquiries", "write:inquiries",
] as const;

const ALL_EVENTS = [
  "proposal.created", "proposal.updated", "proposal.ai_completed",
  "threat.created", "contract.signed", "inquiry.created",
] as const;

type ApiKey = {
  id: string;
  label: string;
  key_prefix: string;
  is_active: boolean;
  scopes: string[];
  rate_limit: number;
  last_used_at: string | null;
  created_at: string;
};

type Webhook = {
  id: string;
  url: string;
  description: string | null;
  event_types: string[];
  secret: string;
  is_active: boolean;
  created_at: string;
};

type Delivery = {
  id: string;
  webhook_id: string;
  event_type: string;
  status: string;
  status_code: number | null;
  attempt: number;
  error_message: string | null;
  created_at: string;
};

async function sha256(input: string) {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(input));
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

export default function EnterpriseApiManager() {
  const { user } = useAuth();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [loading, setLoading] = useState(true);

  // Key dialog
  const [keyDialog, setKeyDialog] = useState(false);
  const [editingKey, setEditingKey] = useState<ApiKey | null>(null);
  const [keyLabel, setKeyLabel] = useState("Default");
  const [keyScopes, setKeyScopes] = useState<string[]>([...ALL_SCOPES]);
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [keyBusy, setKeyBusy] = useState(false);

  // Webhook dialog
  const [hookDialog, setHookDialog] = useState(false);
  const [editingHook, setEditingHook] = useState<Webhook | null>(null);
  const [hookUrl, setHookUrl] = useState("");
  const [hookDesc, setHookDesc] = useState("");
  const [hookEvents, setHookEvents] = useState<string[]>([]);
  const [hookBusy, setHookBusy] = useState(false);

  const fetchAll = async () => {
    if (!user) return;
    setLoading(true);
    const [k, w] = await Promise.all([
      supabase.from("api_keys").select("*").order("created_at", { ascending: false }),
      supabase.from("webhooks").select("*").order("created_at", { ascending: false }),
    ]);
    setKeys((k.data ?? []) as any);
    setWebhooks((w.data ?? []) as any);

    const hookIds = (w.data ?? []).map((h: any) => h.id);
    if (hookIds.length) {
      const { data: d } = await supabase
        .from("webhook_deliveries")
        .select("*")
        .in("webhook_id", hookIds)
        .order("created_at", { ascending: false })
        .limit(50);
      setDeliveries((d ?? []) as any);
    } else {
      setDeliveries([]);
    }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, [user?.id]);

  // ---------- API Keys ----------
  const openCreateKey = () => {
    setEditingKey(null);
    setKeyLabel("Default");
    setKeyScopes([...ALL_SCOPES]);
    setGeneratedKey(null);
    setKeyDialog(true);
  };

  const openEditKey = (k: ApiKey) => {
    setEditingKey(k);
    setKeyLabel(k.label);
    setKeyScopes(k.scopes ?? []);
    setGeneratedKey(null);
    setKeyDialog(true);
  };

  const toggleScope = (scope: string) => {
    setKeyScopes((s) => s.includes(scope) ? s.filter(x => x !== scope) : [...s, scope]);
  };

  const toggleEvent = (evt: string) => {
    setHookEvents((s) => s.includes(evt) ? s.filter(x => x !== evt) : [...s, evt]);
  };

  const saveKey = async () => {
    if (!user) return;
    if (keyScopes.length === 0) { toast.error("Select at least one scope"); return; }
    setKeyBusy(true);
    try {
      if (editingKey) {
        const { error } = await supabase
          .from("api_keys")
          .update({ label: keyLabel, scopes: keyScopes } as any)
          .eq("id", editingKey.id);
        if (error) throw error;
        toast.success("API key updated");
        setKeyDialog(false);
        fetchAll();
      } else {
        const raw = `sf_${crypto.randomUUID().replace(/-/g, "")}`;
        const hash = await sha256(raw);
        const { error } = await supabase.from("api_keys").insert({
          user_id: user.id,
          key_hash: hash,
          key_prefix: raw.substring(0, 10),
          label: keyLabel || "Default",
          scopes: keyScopes,
        } as any);
        if (error) throw error;
        setGeneratedKey(raw);
        toast.success("API key generated — copy it now");
        fetchAll();
      }
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setKeyBusy(false);
    }
  };

  const toggleKeyActive = async (k: ApiKey) => {
    const { error } = await supabase.from("api_keys").update({ is_active: !k.is_active } as any).eq("id", k.id);
    if (error) toast.error(error.message); else { toast.success(k.is_active ? "Deactivated" : "Activated"); fetchAll(); }
  };

  const deleteKey = async (k: ApiKey) => {
    if (!confirm(`Delete key "${k.label}"? This cannot be undone.`)) return;
    const { error } = await supabase.from("api_keys").delete().eq("id", k.id);
    if (error) toast.error(error.message); else { toast.success("Deleted"); fetchAll(); }
  };

  // ---------- Webhooks ----------
  const openCreateHook = () => {
    setEditingHook(null);
    setHookUrl("");
    setHookDesc("");
    setHookEvents([]);
    setHookDialog(true);
  };

  const openEditHook = (h: Webhook) => {
    setEditingHook(h);
    setHookUrl(h.url);
    setHookDesc(h.description ?? "");
    setHookEvents(h.event_types ?? []);
    setHookDialog(true);
  };

  const saveHook = async () => {
    if (!user) return;
    if (!hookUrl.startsWith("https://")) { toast.error("URL must start with https://"); return; }
    if (hookEvents.length === 0) { toast.error("Select at least one event"); return; }
    setHookBusy(true);
    try {
      if (editingHook) {
        const { error } = await supabase.from("webhooks").update({
          url: hookUrl, description: hookDesc || null, event_types: hookEvents,
        } as any).eq("id", editingHook.id);
        if (error) throw error;
        toast.success("Webhook updated");
      } else {
        const { error } = await supabase.from("webhooks").insert({
          user_id: user.id, url: hookUrl, description: hookDesc || null, event_types: hookEvents,
        } as any);
        if (error) throw error;
        toast.success("Webhook created");
      }
      setHookDialog(false);
      fetchAll();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setHookBusy(false);
    }
  };

  const toggleHookActive = async (h: Webhook) => {
    const { error } = await supabase.from("webhooks").update({ is_active: !h.is_active } as any).eq("id", h.id);
    if (error) toast.error(error.message); else fetchAll();
  };

  const deleteHook = async (h: Webhook) => {
    if (!confirm("Delete this webhook? Pending events will not be delivered.")) return;
    const { error } = await supabase.from("webhooks").delete().eq("id", h.id);
    if (error) toast.error(error.message); else { toast.success("Deleted"); fetchAll(); }
  };

  return (
    <div className="space-y-6">
      <Card className="border-border bg-card">
        <CardHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2 text-foreground">
                <Key className="h-5 w-5 text-primary" /> Enterprise API
              </CardTitle>
              <CardDescription>
                Manage scoped API keys and webhook subscriptions. Read the{" "}
                <Link to="/enterprise-api-docs" className="text-primary hover:underline inline-flex items-center gap-1">
                  full docs <ExternalLink className="h-3 w-3" />
                </Link>.
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="keys">
            <TabsList>
              <TabsTrigger value="keys" className="gap-1.5"><Key className="h-3.5 w-3.5" /> API Keys</TabsTrigger>
              <TabsTrigger value="webhooks" className="gap-1.5"><Webhook className="h-3.5 w-3.5" /> Webhooks</TabsTrigger>
              <TabsTrigger value="deliveries" className="gap-1.5">Deliveries</TabsTrigger>
            </TabsList>

            {/* KEYS */}
            <TabsContent value="keys" className="space-y-4">
              <div className="flex justify-end">
                <Button onClick={openCreateKey}><Plus className="h-4 w-4 mr-1.5" /> New Key</Button>
              </div>
              {keys.length === 0 ? (
                <p className="text-center text-muted-foreground py-8 text-sm">No API keys yet.</p>
              ) : (
                <div className="overflow-x-auto -mx-6">
                  <Table className="min-w-[700px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Label</TableHead>
                        <TableHead>Prefix</TableHead>
                        <TableHead>Scopes</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Last used</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {keys.map((k) => (
                        <TableRow key={k.id}>
                          <TableCell className="font-medium">{k.label}</TableCell>
                          <TableCell><code className="text-xs bg-secondary px-2 py-0.5 rounded">{k.key_prefix}…</code></TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1 max-w-[260px]">
                              {(k.scopes ?? []).map((s) => (
                                <Badge key={s} variant="secondary" className="text-[10px]">{s}</Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell>
                            {k.is_active
                              ? <Badge className="bg-success/20 text-success border-success/30">Active</Badge>
                              : <Badge variant="secondary">Inactive</Badge>}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {k.last_used_at ? new Date(k.last_used_at).toLocaleDateString() : "Never"}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button size="sm" variant="ghost" onClick={() => openEditKey(k)}>Edit</Button>
                              <Button size="sm" variant="ghost" onClick={() => toggleKeyActive(k)}>
                                {k.is_active ? "Disable" : "Enable"}
                              </Button>
                              <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => deleteKey(k)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>

            {/* WEBHOOKS */}
            <TabsContent value="webhooks" className="space-y-4">
              <div className="flex justify-end">
                <Button onClick={openCreateHook}><Plus className="h-4 w-4 mr-1.5" /> New Webhook</Button>
              </div>
              {webhooks.length === 0 ? (
                <p className="text-center text-muted-foreground py-8 text-sm">No webhooks yet.</p>
              ) : (
                <div className="space-y-3">
                  {webhooks.map((h) => (
                    <Card key={h.id} className="border-border bg-secondary/30">
                      <CardContent className="p-4 space-y-2">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <code className="text-xs bg-background px-2 py-0.5 rounded break-all">{h.url}</code>
                              {h.is_active
                                ? <Badge className="bg-success/20 text-success border-success/30">Active</Badge>
                                : <Badge variant="secondary">Paused</Badge>}
                            </div>
                            {h.description && <p className="text-xs text-muted-foreground mt-1">{h.description}</p>}
                            <div className="flex flex-wrap gap-1 mt-2">
                              {h.event_types.map((e) => (
                                <Badge key={e} variant="outline" className="text-[10px]">{e}</Badge>
                              ))}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => openEditHook(h)}>Edit</Button>
                            <Button size="sm" variant="ghost" onClick={() => toggleHookActive(h)}>
                              {h.is_active ? "Pause" : "Resume"}
                            </Button>
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => deleteHook(h)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>Signing secret:</span>
                          <code className="bg-background px-2 py-0.5 rounded font-mono break-all">{h.secret}</code>
                          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => { navigator.clipboard.writeText(h.secret); toast.success("Copied"); }}>
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* DELIVERIES */}
            <TabsContent value="deliveries">
              {deliveries.length === 0 ? (
                <p className="text-center text-muted-foreground py-8 text-sm">No deliveries yet.</p>
              ) : (
                <div className="overflow-x-auto -mx-6">
                  <Table className="min-w-[600px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Event</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>HTTP</TableHead>
                        <TableHead>Attempt</TableHead>
                        <TableHead>When</TableHead>
                        <TableHead>Error</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {deliveries.map((d) => (
                        <TableRow key={d.id}>
                          <TableCell><Badge variant="outline" className="text-[10px]">{d.event_type}</Badge></TableCell>
                          <TableCell>
                            {d.status === "success"
                              ? <Badge className="bg-success/20 text-success border-success/30">success</Badge>
                              : <Badge variant="destructive">{d.status}</Badge>}
                          </TableCell>
                          <TableCell className="text-xs">{d.status_code ?? "—"}</TableCell>
                          <TableCell className="text-xs">{d.attempt}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{new Date(d.created_at).toLocaleString()}</TableCell>
                          <TableCell className="text-xs text-muted-foreground max-w-[260px] truncate">{d.error_message ?? "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* KEY DIALOG */}
      <Dialog open={keyDialog} onOpenChange={(o) => { if (!o) { setKeyDialog(false); setGeneratedKey(null); } }}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>{editingKey ? "Edit API Key" : generatedKey ? "API Key Created" : "New API Key"}</DialogTitle>
          </DialogHeader>
          {generatedKey ? (
            <div className="space-y-3">
              <p className="text-sm text-warning">⚠️ Copy this key now — it will not be shown again.</p>
              <div className="flex items-center gap-2">
                <Input readOnly value={generatedKey} className="font-mono text-xs" />
                <Button variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(generatedKey); toast.success("Copied"); }}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label>Label</Label>
                <Input value={keyLabel} onChange={(e) => setKeyLabel(e.target.value)} placeholder="Production" className="mt-1" />
              </div>
              <div>
                <Label className="mb-2 block">Scopes</Label>
                <div className="grid grid-cols-2 gap-2">
                  {ALL_SCOPES.map((s) => (
                    <label key={s} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox checked={keyScopes.includes(s)} onCheckedChange={() => toggleScope(s)} />
                      <code className="text-xs">{s}</code>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setKeyDialog(false); setGeneratedKey(null); }}>
              {generatedKey ? "Done" : "Cancel"}
            </Button>
            {!generatedKey && (
              <Button onClick={saveKey} disabled={keyBusy}>
                {keyBusy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {editingKey ? "Save" : "Generate"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WEBHOOK DIALOG */}
      <Dialog open={hookDialog} onOpenChange={setHookDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>{editingHook ? "Edit Webhook" : "New Webhook"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Endpoint URL</Label>
              <Input value={hookUrl} onChange={(e) => setHookUrl(e.target.value)} placeholder="https://yourdomain.com/webhooks/sf" className="mt-1" />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input value={hookDesc} onChange={(e) => setHookDesc(e.target.value)} placeholder="Production CRM sync" className="mt-1" />
            </div>
            <div>
              <Label className="mb-2 block">Events</Label>
              <div className="grid grid-cols-2 gap-2">
                {ALL_EVENTS.map((e) => (
                  <label key={e} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={hookEvents.includes(e)} onCheckedChange={() => toggleEvent(e)} />
                    <code className="text-xs">{e}</code>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHookDialog(false)}>Cancel</Button>
            <Button onClick={saveHook} disabled={hookBusy}>
              {hookBusy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editingHook ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
